import { IGif } from '@giphy/js-types'
import { Decision } from '../api/placements/types'

const MAX_ANALYTICS_RESPONSE_PAYLOAD_LENGTH = 4096
const MAX_DECODED_ANALYTICS_PAYLOAD_LENGTH = 4096
const AP_PARAM_PLACEHOLDER = '${ap}'

function parseSearchParams(value: string): URLSearchParams {
    return new URLSearchParams(value.startsWith('?') ? value.slice(1) : value)
}

function analyticsPayloadCandidates(payload: string): string[] {
    const candidates = [payload]

    try {
        const payloadUrl = new URL(payload)
        const nestedPayload = payloadUrl.searchParams.get('analytics_response_payload')
        if (nestedPayload) {
            candidates.push(nestedPayload)
        }
    } catch {
        // Payloads are usually query strings, not full URLs.
    }

    const nestedPayload = parseSearchParams(payload).get('analytics_response_payload')
    if (nestedPayload) {
        candidates.push(nestedPayload)
    }

    return Array.from(new Set(candidates))
}

function decodeAnalyticsEventPayload(encodedPayload: string | null): string | undefined {
    if (!encodedPayload || encodedPayload.length > MAX_ANALYTICS_RESPONSE_PAYLOAD_LENGTH) {
        return undefined
    }

    try {
        const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
        const paddingLength = (4 - (normalizedPayload.length % 4)) % 4
        const decodedPayload = atob(`${normalizedPayload}${'='.repeat(paddingLength)}`)

        if (decodedPayload.length > MAX_DECODED_ANALYTICS_PAYLOAD_LENGTH) {
            return undefined
        }

        return decodedPayload
    } catch {
        return undefined
    }
}

function hasKoddiFingerprintParams(payload: string | undefined): boolean {
    if (!payload || payload.length > MAX_ANALYTICS_RESPONSE_PAYLOAD_LENGTH) {
        return false
    }

    const params = parseSearchParams(payload)
    const promotedBidder = params.get('pb')
    return !!promotedBidder?.startsWith('kd.')
}

function hasKoddiAnalyticsPayload(payload: unknown): boolean {
    if (typeof payload !== 'string' || payload.length === 0 || payload.length > MAX_ANALYTICS_RESPONSE_PAYLOAD_LENGTH) {
        return false
    }

    return analyticsPayloadCandidates(payload).some((candidate) => {
        if (hasKoddiFingerprintParams(candidate)) {
            return true
        }

        const decodedPayload = decodeAnalyticsEventPayload(parseSearchParams(candidate).get('e'))
        return hasKoddiFingerprintParams(decodedPayload)
    })
}

/**
 * Returns true if the GIF has a legacy Bouncer ad marker.
 */
export const isAdGif = (gif: IGif): boolean => {
    const result = !!(gif.bottle_data && Object.keys(gif.bottle_data).length > 0)
    return result
}

/**
 * Returns true if the GIF has a known AdsBidding API-result ad marker.
 */
export const isAdsBiddingApiAdGif = (gif: IGif): boolean => {
    return isAdGif(gif) || hasKoddiAnalyticsPayload(gif.analytics_response_payload)
}

/**
 * Returns true if the GIF has a Koddi API-result ad marker.
 */
export const isKoddiApiAdGif = (gif: IGif): boolean => {
    return hasKoddiAnalyticsPayload(gif.analytics_response_payload)
}

/**
 * Returns true if the GifResults contains at least one legacy Bouncer ad GIF.
 */
export const containsAdGif = (gifs: IGif[] | null | undefined): boolean => {
    if (!gifs || gifs.length === 0) {
        return false
    }
    return gifs.some((gif) => isAdGif(gif))
}

/**
 * Returns true if the GifResults contains at least one AdsBidding API-result ad GIF.
 */
export const containsAdsBiddingApiAdGif = (gifs: IGif[] | null | undefined): boolean => {
    if (!gifs || gifs.length === 0) {
        return false
    }
    return gifs.some((gif) => isAdsBiddingApiAdGif(gif))
}

/**
 * Updates data within an ad decision.
 * Modifies bottle_data and Decision objects.
 * @param AdDecision The decision to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 * @param adProductType The type of ad product (default: undefined)
 * @returns The modified AdDecision object.
 */
export const processDecision = (
    decision?: Decision,
    appendAp: boolean = true,
    adProductType?: string
): Decision | undefined => {
    if (!decision || !decision.gif) return

    if (decision.click_url) {
        const gif = { ...decision.gif }
        gif.bottle_data = gif.bottle_data ?? {}
        gif.bottle_data.tdata = gif.bottle_data.tdata ?? {}
        gif.bottle_data.tdata.click_out_url = decision.click_url
    }
    const processedGif =
        decision.gif.type === 'video'
            ? processGifVideoAssets(decision.gif, appendAp)
            : processGifImages(decision.gif, appendAp)

    // for subdecisions on xl_carousel_banner, don't append ap=1 to avoid showing "Promoted" pill
    const shouldAppendApForSubdecisions = decision.subdecisions && adProductType !== 'xl_carousel_banner'

    const processedSubdecisions: Decision[] | undefined =
        shouldAppendApForSubdecisions && decision.subdecisions
            ? decision.subdecisions.map((sub: Decision) => (sub ? processDecision(sub, appendAp, adProductType)! : sub))
            : decision.subdecisions

    return {
        ...decision,
        gif: processedGif,
        subdecisions: processedSubdecisions,
    }
}

/**
 * Appends the 'ap=1' query parameter to a given URL.
 * This is used to indicate that the asset is being used in an ad.
 * @param url The URL to modify.
 * @returns The modified URL with the 'ap=1' query parameter.
 */
export const appendApParam = (url: string) => {
    try {
        const u = new URL(url)
        const apParam = u.searchParams.get('ap')
        if (apParam === AP_PARAM_PLACEHOLDER) {
            u.searchParams.set('ap', '1')
        }
        // Avoid duplicating if already present
        if (!u.searchParams.has('ap')) {
            u.searchParams.append('ap', '1')
        }
        return u.toString()
    } catch {
        // Fallback for non-standard URLs
        const placeholderApParam = /([?&]ap=)\$\{ap\}(?=([&#]|$))/
        if (placeholderApParam.test(url)) {
            return url.replace(placeholderApParam, (_match, prefix: string) => `${prefix}1`)
        }
        const hasQuery = url.includes('?')
        const hasAp = /[?&]ap=1(?!\d)/.test(url)
        return hasAp ? url : `${url}${hasQuery ? '&' : '?'}ap=1`
    }
}

/**
 * Processes a GIF's images to append the 'ap=1' parameter to all image URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifImages = (gif: IGif, appendAp: boolean = true) => {
    if (!gif?.images || !appendAp) return gif

    const processedImages = Object.fromEntries(
        Object.entries(gif.images).map(([imageKey, imageValue]) => {
            if (!imageValue || typeof imageValue !== 'object') {
                return [imageKey, imageValue]
            }
            const processedImage = Object.fromEntries(
                Object.entries(imageValue).map(([key, value]) => {
                    if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                        return [key, appendApParam(value)]
                    }
                    return [key, value]
                })
            )
            return [imageKey, processedImage]
        })
    )
    return { ...gif, images: processedImages } as IGif
}

/**
 * Processes a GIF's video assets to append the 'ap=1' parameter to all asset URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifVideoAssets = (gif: IGif, appendAp: boolean = true) => {
    if (!gif.video?.assets || !appendAp) return gif

    const processedAssets = Object.fromEntries(
        Object.entries(gif.video.assets).map(([assetKey, assetValue]) => {
            if (!assetValue || typeof assetValue !== 'object') {
                return [assetKey, assetValue]
            }
            const processedAsset = Object.fromEntries(
                Object.entries(assetValue).map(([key, value]) => {
                    if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                        return [key, appendApParam(value)]
                    }
                    return [key, value]
                })
            )

            return [assetKey, processedAsset]
        })
    )
    return { ...gif, video: { ...gif.video, assets: processedAssets } } as IGif
}
